import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { escapeOrIlikeTerm } from '../../lib/postgrestEscape';
import { useApp } from '../../hooks/useApp';
import {
  createNotification,
  maybeFireLowCreditWarning,
  notifySubmitterAndAdmins,
} from '../../lib/notifications';
import { triggerTicker } from '../../lib/activityTicker';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import ExportButton from '../../components/ui/ExportButton';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import ArbiDatePicker from '../../components/ui/ArbiDatePicker';
import CancelPOModal, { type CancelPOPayload } from '../../components/purchasing/CancelPOModal';
import { Toast, type ToastState } from '../../components/ui/Toast';
import type { POStatus } from '../../types';
import type { PostgrestError } from '@supabase/supabase-js';
import { extractEnvelope } from '../../lib/extractEnvelope';

ModuleRegistry.registerModules([AllCommunityModule]);

// Loose RPC caller — approve_purchase_order is called with dynamic args.
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Status badge config ─────────────────────────────────────────────────────

interface StatusConfig {
  label: string;
  cls: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  draft: { label: 'Draft', cls: 'bg-surface-2 text-ink-muted' },
  pending_review: { label: 'Pending Review', cls: 'bg-amber-400/10 text-amber-400' },
  rejected: { label: 'Rejected', cls: 'bg-red-400/10 text-red-400' },
  approved: { label: 'Approved', cls: 'bg-blue-400/10 text-blue-400' },
  ordered: { label: 'Ordered', cls: 'bg-purple-400/10 text-purple-400' },
  awaiting_invoice: { label: 'Awaiting Invoice', cls: 'bg-yellow-400/10 text-yellow-400' },
  processing: { label: 'Processing', cls: 'bg-cyan-400/10 text-cyan-400' },
  invoice_received: { label: 'Invoice Received', cls: 'bg-teal-400/10 text-teal-400' },
  partially_received: { label: 'Partial', cls: 'bg-green-400/10 text-green-400' },
  received: { label: 'Received', cls: 'bg-green-400/15 text-green-300 font-semibold' },
  closed: { label: 'Closed', cls: 'bg-surface-2 text-ink-muted' },
  superseded: { label: 'Superseded', cls: 'bg-surface-2 text-ink-muted italic' },
  cancelled: { label: 'Cancelled', cls: 'bg-surface-2 text-ink-muted line-through' },
  discrepancy: { label: 'Discrepancy', cls: 'bg-red-400/10 text-red-400' },
};

interface StatusFilterTab {
  key: string;
  label: string;
}

const STATUS_FILTER_TABS: StatusFilterTab[] = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'pending_review', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'awaiting_invoice', label: 'Awaiting Inv.' },
  { key: 'processing', label: 'Processing' },
  { key: 'received', label: 'Received' },
  { key: 'closed', label: 'Closed' },
];

// Row shape in the grid — matches the SELECT list + nested distributors join.
interface POGridRow {
  id: string;
  po_number: string;
  status: POStatus;
  order_date: string | null;
  expected_delivery_date: string | null;
  total_cost: number | null;
  expected_profit: number | null;
  total_skus: number | null;
  total_units: number | null;
  fulfillment_path: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
  created_at: string;
  created_by?: string | null;
  distributor_id: string;
  distributors?: { name: string | null } | null;
}

type BulkAction = 'approve' | 'cancel' | null;

/** Row-level contextual actions that use a simple ConfirmDialog (no modal form). */
type RowAction =
  | 'submit'
  | 'delete'
  | 'cancel'
  | 'markOrdered'
  | 'markAwaitingInvoice'
  | 'markProcessing'
  | 'markInvoiceReceived'
  | 'editRequired';

interface RowActionConfirmState {
  action: RowAction;
  po: POGridRow;
}

// ── PurchaseOrders page ─────────────────────────────────────────────────────

export default function PurchaseOrders(): JSX.Element {
  // Day 64.5 V-ROUI: viewer reduces ActionsCell to View-only (no Edit, Approve,
  // Reject, Cancel, Begin Receiving, etc.) and hides the bulk action bar.
  // Per DOMAIN_ARCHITECTURE §5 — viewer view-only on Purchasing, dist_rep
  // denied at route. Viewer is the only V-ROUI consumer here.
  const { user, isAdmin, isViewer, isDistRep, profileDistributorId } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gridRef = useRef<AgGridReact<POGridRow> | null>(null);
  const gridApiRef = useRef<GridApi<POGridRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>(
    () => searchParams.get('status') || 'all'
  );
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [approveConfirm, setApproveConfirm] = useState<POGridRow | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [selectedRows, setSelectedRows] = useState<POGridRow[]>([]);
  const [bulkAction, setBulkAction] = useState<BulkAction>(null);
  const [bulkProcessing, setBulkProcessing] = useState<boolean>(false);
  const [rowActionConfirm, setRowActionConfirm] = useState<RowActionConfirmState | null>(null);
  const [rejectTarget, setRejectTarget] = useState<POGridRow | null>(null);
  const [rejectNote, setRejectNote] = useState<string>('');

  // State ref for getRows
  const stateRef = useRef<{
    searchText: string;
    statusFilter: string;
    dateFrom: string;
    dateTo: string;
    _isDistRep: boolean;
    _distributorId: string | null;
  }>({
    searchText,
    statusFilter,
    dateFrom,
    dateTo,
    _isDistRep: isDistRep,
    _distributorId: profileDistributorId,
  });
  stateRef.current = {
    searchText,
    statusFilter,
    dateFrom,
    dateTo,
    _isDistRep: isDistRep,
    _distributorId: profileDistributorId,
  };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, sortModel, successCallback, failCallback } = params;
    const { searchText: st, statusFilter: sf, dateFrom: df, dateTo: dt } = stateRef.current;

    try {
      let query = supabase
        .from('purchase_orders')
        .select(
          'id, po_number, status, order_date, expected_delivery_date, total_cost, expected_profit, total_skus, total_units, fulfillment_path, submitted_by, submitted_at, created_at, distributor_id, distributors:distributor_id ( name )',
          { count: 'exact' }
        );

      if (stateRef.current._isDistRep && stateRef.current._distributorId) {
        query = query.eq('distributor_id', stateRef.current._distributorId);
      }

      if (sf !== 'all') {
        query = query.eq('status', sf as POStatus);
      }

      if (st.trim()) {
        // Day 76 P5.1 Phase 9A — escapeOrIlikeTerm adoption (Day 73 P4.1)
        const term = escapeOrIlikeTerm(st.trim());
        query = query.or(`po_number.ilike.${term}`);
      }

      if (df) query = query.gte('order_date', df);
      if (dt) query = query.lte('order_date', dt);

      if (sortModel.length > 0) {
        const s = sortModel[0];
        query = query.order(s.colId, { ascending: s.sort === 'asc', nullsFirst: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(startRow, endRow - 1);
      const { data, count, error } = await query;
      if (error) throw error;

      successCallback((data ?? []) as unknown as POGridRow[], count || 0);
    } catch (e) {
      reportDatasourceFailure(
        'Purchase Orders',
        { rpcName: 'purchase_orders', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (event: GridReadyEvent<POGridRow>): void => {
      gridApiRef.current = event.api;
      event.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, statusFilter, dateFrom, dateTo, getRowsFn]);

  // ── Toast auto-dismiss ──────────────────────────────────────

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Approve action ──────────────────────────────────────────

  const handleApprovePO = useCallback(
    async (po: POGridRow): Promise<void> => {
      // T2.1 — atomic approve guard RPC (from-status + credit-cap + single
      // soft_reserve). Replaces the prior update + separate credit fire (no
      // cap; could double-fire). A cap-exceeded / already-moved PO surfaces
      // the userError as an error toast.
      const env = extractEnvelope<{ status: string }>(
        await callRpc('approve_purchase_order', { p_po_id: po.id })
      );
      if (!env.isOk) {
        setToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Failed to approve' });
        setApproveConfirm(null);
        return;
      }
      maybeFireLowCreditWarning(po.distributor_id, po.distributors?.name ?? 'Distributor');
      setToast({ kind: 'success', message: `PO ${po.po_number} approved` });
      triggerTicker('PO approved');
      const recipientId = po.submitted_by || po.created_by;
      if (recipientId && recipientId !== user?.id) {
        createNotification({
          userId: recipientId,
          event: 'po_approved',
          title: 'PO Approved',
          message: `PO ${po.po_number} has been approved and soft reserve applied.`,
          link: `/purchasing/purchase-orders/${po.id}`,
        });
      }
      if (gridApiRef.current) {
        gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
      }
      setApproveConfirm(null);
    },
    [getRowsFn, user?.id]
  );

  // ── Row-level contextual action handler ─────────────────────
  // Handles the simple status-transition actions that don't require a
  // bespoke form (submit, cancel, delete-draft, markProcessing,
  // markInvoiceReceived). Complex actions (editRequired, reject-with-note)
  // are routed through PODetail / the inline reject modal below.

  const refreshGrid = useCallback((): void => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [getRowsFn]);

  const handleRowAction = useCallback(async (): Promise<void> => {
    if (!rowActionConfirm) return;
    const { action, po } = rowActionConfirm;

    if (action === 'submit') {
      const { error } = await supabase
        .from('purchase_orders')
        .update({
          status: 'pending_review',
          submitted_by: user?.id ?? null,
          submitted_at: new Date().toISOString(),
        })
        .eq('id', po.id);
      if (error) setToast({ kind: 'error', message: `Failed to submit: ${error.message}` });
      else {
        setToast({ kind: 'success', message: `PO ${po.po_number} submitted for review` });
        const { data: admins } = await supabase.from('profiles').select('id').eq('role', 'admin');
        for (const a of (admins ?? []) as { id: string }[]) {
          createNotification({
            userId: a.id,
            event: 'po_submitted',
            title: 'PO Submitted',
            message: `PO ${po.po_number} submitted for review.`,
            link: `/purchasing/purchase-orders/${po.id}`,
          });
        }
        refreshGrid();
      }
    } else if (action === 'delete') {
      // Hard delete is only allowed for drafts — nothing downstream depends on a draft.
      const { error: liErr } = await supabase.from('po_line_items').delete().eq('po_id', po.id);
      if (liErr) {
        setToast({ kind: 'error', message: `Failed to delete line items: ${liErr.message}` });
      } else {
        const { error } = await supabase.from('purchase_orders').delete().eq('id', po.id);
        if (error) setToast({ kind: 'error', message: `Failed to delete: ${error.message}` });
        else {
          setToast({ kind: 'success', message: `PO ${po.po_number} deleted` });
          refreshGrid();
        }
      }
    } else if (action === 'cancel') {
      const { error } = await supabase
        .from('purchase_orders')
        .update({ status: 'cancelled' })
        .eq('id', po.id);
      if (error) setToast({ kind: 'error', message: `Failed to cancel: ${error.message}` });
      else {
        // DD-04: the cancel reserve_release moved SERVER-SIDE — trg_release_reserve_on_cancel
        // fires it atomically on status→cancelled (this direct .update() path included).
        setToast({ kind: 'success', message: `PO ${po.po_number} cancelled` });
        refreshGrid();
      }
    } else if (action === 'markOrdered') {
      // Approved → Ordered. Soft reserve already applied at Approve; no
      // ledger entry here. Pure status update per CLAUDE_REFERENCE.md §7.
      const { error } = await supabase
        .from('purchase_orders')
        .update({ status: 'ordered' })
        .eq('id', po.id);
      if (error) setToast({ kind: 'error', message: `Failed: ${error.message}` });
      else {
        setToast({ kind: 'success', message: `PO ${po.po_number} marked Ordered` });
        notifySubmitterAndAdmins({
          submitterUserId: po.submitted_by ?? po.created_by,
          event: 'po_ordered',
          title: `PO ${po.po_number} ordered`,
          message: `PO ${po.po_number} has been placed with the distributor.`,
          link: `/purchasing/purchase-orders/${po.id}`,
        });
        refreshGrid();
      }
    } else if (action === 'markAwaitingInvoice') {
      // Ordered → Awaiting Invoice. Per §7 this is the canonical hand-off
      // into the invoice-tracking hold stage; Processing owns the actual
      // invoice-upload + cost-entry step that gates Invoice Received.
      const { error } = await supabase
        .from('purchase_orders')
        .update({ status: 'awaiting_invoice' })
        .eq('id', po.id);
      if (error) setToast({ kind: 'error', message: `Failed: ${error.message}` });
      else {
        setToast({ kind: 'success', message: `PO ${po.po_number} moved to Awaiting Invoice` });
        refreshGrid();
      }
    } else if (action === 'markProcessing') {
      const { error } = await supabase
        .from('purchase_orders')
        .update({ status: 'processing' })
        .eq('id', po.id);
      if (error) setToast({ kind: 'error', message: `Failed: ${error.message}` });
      else {
        setToast({ kind: 'success', message: `PO ${po.po_number} moved to Processing` });
        refreshGrid();
      }
    } else if (action === 'markInvoiceReceived') {
      const { error } = await supabase
        .from('purchase_orders')
        .update({ status: 'invoice_received' })
        .eq('id', po.id);
      if (error) setToast({ kind: 'error', message: `Failed: ${error.message}` });
      else {
        setToast({
          kind: 'success',
          message: `PO ${po.po_number} moved to Invoice Received — ready for receiving`,
        });
        refreshGrid();
      }
    } else if (action === 'editRequired') {
      // The revision-creation flow lives in PODetail — navigate there with
      // a query flag so the confirm dialog opens automatically.
      navigate(`/purchasing/purchase-orders/${po.id}?action=editRequired`);
    }

    setRowActionConfirm(null);
  }, [rowActionConfirm, user?.id, refreshGrid, navigate]);

  // ── Reject handler (uses inline modal with note) ────────────
  const handleReject = useCallback(async (): Promise<void> => {
    if (!rejectTarget) return;
    const { error } = await supabase
      .from('purchase_orders')
      .update({ status: 'rejected' })
      .eq('id', rejectTarget.id);
    if (error) {
      setToast({ kind: 'error', message: `Failed to reject: ${error.message}` });
    } else {
      // Capture rejection rationale into entity_notes (replaces former
      // purchase_orders.notes scalar write).
      if (rejectNote.trim()) {
        const noteRpcResult = await supabase.rpc('create_entity_note', {
          p_entity_type: 'purchase_order',
          p_entity_id: rejectTarget.id,
          p_text: rejectNote.trim(),
          p_visibility: 'all_internal',
        });
        const noteEnv = extractEnvelope<unknown>(noteRpcResult);
        if (!noteEnv.isOk) {
          console.error(
            '[PurchaseOrders] reject-note envelope failure:',
            noteEnv.userErrors[0]?.message
          );
        }
      }
      setToast({ kind: 'success', message: `PO ${rejectTarget.po_number} rejected` });
      const recipientId = rejectTarget.submitted_by || rejectTarget.created_by;
      if (recipientId) {
        createNotification({
          userId: recipientId,
          event: 'po_rejected',
          title: 'PO Rejected',
          message: `PO ${rejectTarget.po_number} was rejected.${rejectNote ? ' Note: ' + rejectNote : ''}`,
          link: `/purchasing/purchase-orders/${rejectTarget.id}`,
        });
      }
      refreshGrid();
    }
    setRejectTarget(null);
    setRejectNote('');
  }, [rejectTarget, rejectNote, refreshGrid]);

  // ── Bulk action handlers ────────────────────────────────────

  const handleBulkApprove = useCallback(async (): Promise<void> => {
    const approvable = selectedRows.filter((r) => r.status === 'pending_review');
    if (approvable.length === 0) return;
    setBulkProcessing(true);
    let count = 0;
    const failures: string[] = [];
    for (const po of approvable) {
      // T2.1 — route bulk approve through the atomic guard RPC so the cap +
      // from-status checks apply per-PO. A cap-exceeded / already-moved PO is
      // surfaced, not silently skipped.
      const env = extractEnvelope<{ status: string }>(
        await callRpc('approve_purchase_order', { p_po_id: po.id })
      );
      if (env.isOk) {
        count++;
        maybeFireLowCreditWarning(po.distributor_id, po.distributors?.name ?? 'Distributor');
        const recipientId = po.submitted_by || po.created_by;
        if (recipientId && recipientId !== user?.id) {
          createNotification({
            userId: recipientId,
            event: 'po_approved',
            title: 'PO Approved',
            message: `PO ${po.po_number} has been approved.`,
            link: `/purchasing/purchase-orders/${po.id}`,
          });
        }
      } else {
        failures.push(`${po.po_number}: ${env.userErrors[0]?.message ?? 'failed'}`);
      }
    }
    setBulkProcessing(false);
    setBulkAction(null);
    if (failures.length > 0) {
      setToast({
        kind: 'error',
        message: `${count} approved, ${failures.length} blocked — ${failures[0]}${
          failures.length > 1 ? ` (+${failures.length - 1} more)` : ''
        }`,
      });
    } else {
      setToast({ kind: 'success', message: `${count} PO${count !== 1 ? 's' : ''} approved` });
    }
    if (count > 0) triggerTicker(`${count} POs approved`);
    setSelectedRows([]);
    if (gridApiRef.current) {
      gridApiRef.current.deselectAll();
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [selectedRows, getRowsFn, user?.id]);

  const handleBulkCancel = useCallback(
    async (payload: CancelPOPayload): Promise<void> => {
      const cancellable = selectedRows.filter((r) =>
        (['draft', 'pending_review', 'approved'] as POStatus[]).includes(r.status)
      );
      if (cancellable.length === 0) return;
      setBulkProcessing(true);
      let count = 0;
      for (const po of cancellable) {
        const { error } = await supabase
          .from('purchase_orders')
          .update({
            status: 'cancelled',
            cancellation_reason: payload.reason,
            cancellation_other_text: payload.otherText,
          } as never)
          .eq('id', po.id);
        if (!error) {
          count++;
          // DD-04: the cancel reserve_release moved SERVER-SIDE — trg_release_reserve_on_cancel
          // fires it atomically on status→cancelled (this bulk direct-.update() path included).
          // Day 56 item 8 — notify flags applied uniformly across the batch.
          // Submitter notification gated on notify_submitter; dist_rep
          // notification gated on notify_distributor.
          if (payload.notifySubmitter && po.submitted_by) {
            createNotification({
              userId: po.submitted_by,
              event: 'po_cancelled',
              title: 'PO Cancelled',
              message: `PO ${po.po_number} has been cancelled.`,
              link: `/purchasing/purchase-orders/${po.id}`,
            });
          }
        }
      }
      setBulkProcessing(false);
      setBulkAction(null);
      setToast({ kind: 'success', message: `${count} PO${count !== 1 ? 's' : ''} cancelled` });
      setSelectedRows([]);
      if (gridApiRef.current) {
        gridApiRef.current.deselectAll();
        gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
      }
    },
    [selectedRows, getRowsFn]
  );

  const onSelectionChanged = useCallback((): void => {
    if (gridApiRef.current) {
      setSelectedRows(gridApiRef.current.getSelectedRows());
    }
  }, []);

  // ── Cell renderers ──────────────────────────────────────────

  type RowParams = ICellRendererParams<POGridRow>;

  const PONumberCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/purchasing/purchase-orders/${p.data!.id}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {p.data.po_number}
        </button>
      );
    },
    [navigate]
  );

  const DistributorCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    return <span className="text-ink-secondary">{p.data.distributors?.name || '—'}</span>;
  }, []);

  const StatusCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const s = STATUS_CONFIG[p.data.status] || STATUS_CONFIG.draft;
    return (
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', s.cls)}>{s.label}</span>
    );
  }, []);

  const EstProfitCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const val = p.data.expected_profit;
    return (
      <span className={clsx('font-mono', val && Number(val) > 0 ? 'text-success' : 'text-danger')}>
        {fmt$(val)}
      </span>
    );
  }, []);

  // Chip styles for the contextual action set rendered in ActionsCell.
  const CHIP_BASE =
    'px-1.5 py-0.5 rounded text-2xs font-medium transition-colors cursor-pointer whitespace-nowrap';
  const CHIP_PRIMARY = 'bg-accent/15 text-accent hover:bg-accent/25';
  const CHIP_GHOST = 'bg-surface-4 text-ink-muted hover:text-ink-primary';
  const CHIP_DANGER = 'bg-danger/15 text-danger hover:bg-danger/25';

  const ActionsCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      const row = p.data;
      const stop = (e: React.MouseEvent): void => e.stopPropagation();
      const viewBtn = (
        <button
          onClick={(e) => {
            stop(e);
            navigate(`/purchasing/purchase-orders/${row.id}`);
          }}
          className={clsx(CHIP_BASE, CHIP_GHOST)}
        >
          View
        </button>
      );
      // Day 64.5 V-ROUI: viewer collapses every status's action set to
      // View-only. Detail page (PODetail.tsx, Phase 4.4) gates its own
      // mutation buttons separately.
      if (isViewer) {
        return <div className="flex items-center gap-1">{viewBtn}</div>;
      }
      const goEdit = (): void => {
        navigate(`/purchasing/purchase-orders/${row.id}/edit`);
      };
      const goReceiving = (): void => {
        navigate(`/purchasing/receiving/${row.id}`);
      };

      // Each status renders its own contextual action set per the PO workflow
      // spec (CLAUDE_REFERENCE.md Section 7). Admin-only actions gate on isAdmin.
      switch (row.status) {
        case 'draft':
          return (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  stop(e);
                  goEdit();
                }}
                className={clsx(CHIP_BASE, CHIP_GHOST)}
              >
                Edit
              </button>
              <button
                onClick={(e) => {
                  stop(e);
                  setRowActionConfirm({ action: 'submit', po: row });
                }}
                className={clsx(CHIP_BASE, CHIP_PRIMARY)}
              >
                Submit
              </button>
              <button
                onClick={(e) => {
                  stop(e);
                  setRowActionConfirm({ action: 'delete', po: row });
                }}
                className={clsx(CHIP_BASE, CHIP_DANGER)}
              >
                Delete
              </button>
            </div>
          );
        case 'pending_review':
          return (
            <div className="flex items-center gap-1">
              {isAdmin && (
                <>
                  <button
                    onClick={(e) => {
                      stop(e);
                      setApproveConfirm(row);
                    }}
                    className={clsx(CHIP_BASE, CHIP_PRIMARY)}
                  >
                    Approve
                  </button>
                  <button
                    onClick={(e) => {
                      stop(e);
                      setRejectTarget(row);
                    }}
                    className={clsx(CHIP_BASE, CHIP_DANGER)}
                  >
                    Reject
                  </button>
                </>
              )}
              {viewBtn}
            </div>
          );
        case 'rejected':
          // Submitter may edit & resubmit a rejected PO.
          return (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  stop(e);
                  goEdit();
                }}
                className={clsx(CHIP_BASE, CHIP_GHOST)}
              >
                Edit
              </button>
              {viewBtn}
            </div>
          );
        case 'approved':
          return (
            <div className="flex items-center gap-1">
              {viewBtn}
              {isAdmin && (
                <>
                  <button
                    onClick={(e) => {
                      stop(e);
                      setRowActionConfirm({ action: 'markOrdered', po: row });
                    }}
                    className={clsx(CHIP_BASE, CHIP_PRIMARY)}
                  >
                    Mark Ordered
                  </button>
                  <button
                    onClick={(e) => {
                      stop(e);
                      setRowActionConfirm({ action: 'editRequired', po: row });
                    }}
                    className={clsx(CHIP_BASE, CHIP_GHOST)}
                  >
                    Edit Required
                  </button>
                </>
              )}
            </div>
          );
        case 'ordered':
          // Per CLAUDE_REFERENCE.md §7 the canonical forward path is
          // ordered → awaiting_invoice (then → processing → invoice_received).
          // No skip to invoice_received. Admin-only.
          return (
            <div className="flex items-center gap-1">
              {viewBtn}
              {isAdmin && (
                <button
                  onClick={(e) => {
                    stop(e);
                    setRowActionConfirm({ action: 'markAwaitingInvoice', po: row });
                  }}
                  className={clsx(CHIP_BASE, CHIP_PRIMARY)}
                >
                  Mark Awaiting Invoice
                </button>
              )}
            </div>
          );
        case 'awaiting_invoice':
          return (
            <div className="flex items-center gap-1">
              {viewBtn}
              <button
                onClick={(e) => {
                  stop(e);
                  setRowActionConfirm({ action: 'markProcessing', po: row });
                }}
                className={clsx(CHIP_BASE, CHIP_PRIMARY)}
              >
                Mark Processing
              </button>
            </div>
          );
        case 'processing':
          return (
            <div className="flex items-center gap-1">
              {viewBtn}
              <button
                onClick={(e) => {
                  stop(e);
                  setRowActionConfirm({ action: 'markInvoiceReceived', po: row });
                }}
                className={clsx(CHIP_BASE, CHIP_PRIMARY)}
              >
                Mark Invoice Received
              </button>
            </div>
          );
        case 'invoice_received':
          return (
            <div className="flex items-center gap-1">
              {viewBtn}
              <button
                onClick={(e) => {
                  stop(e);
                  goReceiving();
                }}
                className={clsx(CHIP_BASE, CHIP_PRIMARY)}
              >
                Begin Receiving
              </button>
            </div>
          );
        case 'partially_received':
          return (
            <div className="flex items-center gap-1">
              {viewBtn}
              <button
                onClick={(e) => {
                  stop(e);
                  goReceiving();
                }}
                className={clsx(CHIP_BASE, CHIP_PRIMARY)}
              >
                Continue Receiving
              </button>
            </div>
          );
        case 'discrepancy':
          return (
            <div className="flex items-center gap-1">
              {viewBtn}
              <button
                onClick={(e) => {
                  stop(e);
                  navigate('/purchasing/discrepancies');
                }}
                className={clsx(CHIP_BASE, CHIP_PRIMARY)}
              >
                Resolve Discrepancy
              </button>
            </div>
          );
        case 'received':
        case 'closed':
        case 'superseded':
        case 'cancelled':
        default:
          // Terminal / read-only statuses — only View.
          return <div className="flex items-center gap-1">{viewBtn}</div>;
      }
    },
    [navigate, isAdmin, isViewer]
  );

  // ── Column definitions ──────────────────────────────────────

  const dashIfEmpty = (p: ValueFormatterParams<POGridRow>): string =>
    p.value === null || p.value === undefined || p.value === '' ? '\u2014' : String(p.value);

  const columnDefs = useMemo<ColDef<POGridRow>[]>(
    () => [
      {
        headerName: 'PO Number',
        field: 'po_number',
        width: 150,
        pinned: 'left',
        suppressMovable: true,
        resizable: false,
        cellRenderer: PONumberCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Distributor',
        flex: 1,
        minWidth: 130,
        cellRenderer: DistributorCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 140,
        cellRenderer: StatusCell,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        } as CellStyle,
      },
      {
        headerName: 'Order Date',
        field: 'order_date',
        width: 110,
        valueFormatter: (p: ValueFormatterParams<POGridRow>) =>
          p.value ? new Date(p.value as string).toLocaleDateString() : '—',
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-secondary)',
        } as CellStyle,
      },
      {
        headerName: 'Expected Delivery',
        field: 'expected_delivery_date',
        width: 130,
        valueFormatter: (p: ValueFormatterParams<POGridRow>) =>
          p.value ? new Date(p.value as string).toLocaleDateString() : '—',
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-secondary)',
        } as CellStyle,
      },
      {
        headerName: 'Total Cost',
        field: 'total_cost',
        width: 110,
        valueFormatter: (p: ValueFormatterParams<POGridRow>) => fmt$(p.value as number | null),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Est. Profit',
        field: 'expected_profit',
        width: 110,
        cellRenderer: EstProfitCell,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'SKUs',
        field: 'total_skus',
        width: 70,
        minWidth: 70,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-secondary)',
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Units',
        field: 'total_units',
        width: 70,
        minWidth: 70,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-secondary)',
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Actions',
        width: 220,
        minWidth: 220,
        sortable: false,
        filter: false,
        cellRenderer: ActionsCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
    ],
    [PONumberCell, DistributorCell, StatusCell, EstProfitCell, ActionsCell]
  );

  const defaultColDef = useMemo<ColDef<POGridRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)', width: 1, style: 'solid' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Purchase Orders</h1>
            {selectedRows.length > 0 && !isViewer && (
              <div className="flex items-center gap-2 ml-2">
                <span className="text-xs text-ink-muted">{selectedRows.length} selected</span>
                {isAdmin && selectedRows.some((r) => r.status === 'pending_review') && (
                  <button
                    onClick={() => setBulkAction('approve')}
                    disabled={bulkProcessing}
                    className="arbi-btn-primary text-2xs px-2 py-1"
                  >
                    Approve Selected
                  </button>
                )}
                {selectedRows.some((r) =>
                  (['draft', 'pending_review', 'approved'] as POStatus[]).includes(r.status)
                ) && (
                  <button
                    onClick={() => setBulkAction('cancel')}
                    disabled={bulkProcessing}
                    className="arbi-btn-ghost text-2xs px-2 py-1 text-danger hover:text-danger"
                  >
                    Cancel Selected
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-purchase-orders" />
            <button
              onClick={() => navigate('/purchasing/po-builder')}
              className="arbi-btn-primary text-xs"
            >
              New PO
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Status filter — LEFT */}
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {STATUS_FILTER_TABS.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={clsx(
                  'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap',
                  statusFilter === s.key
                    ? 'bg-surface-4 text-ink-bright'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Date range filter */}
          <div className="flex items-center gap-1 ml-1">
            <span id="po-date-from-label" className="sr-only">
              From date
            </span>
            <div className="w-[140px]">
              <ArbiDatePicker
                value={dateFrom || null}
                onChange={(v) => setDateFrom(v ?? '')}
                placeholder="From"
                size="xs"
                clearable
                max={dateTo || undefined}
              />
            </div>
            <span className="text-2xs text-ink-muted">–</span>
            <span id="po-date-to-label" className="sr-only">
              To date
            </span>
            <div className="w-[140px]">
              <ArbiDatePicker
                value={dateTo || null}
                onChange={(v) => setDateTo(v ?? '')}
                placeholder="To"
                size="xs"
                clearable
                min={dateFrom || undefined}
              />
            </div>
          </div>

          {/* Search — RIGHT */}
          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search PO number..."
              aria-label="Search purchase orders"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {/* Dist_rep without distributor assignment error */}
      {isDistRep && !profileDistributorId && (
        <div className="px-4 py-3 bg-danger/10 border-b border-danger/30 text-sm text-danger">
          Your account is not assigned to a distributor — contact your administrator.
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact<POGridRow>
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={200}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          overlayNoRowsTemplate='<span style="color:var(--ink-muted);font-size:13px">No purchase orders yet — build your first PO in the Buying Session</span>'
        />
      </div>

      {/* Bulk approve confirm */}
      <ConfirmDialog
        open={bulkAction === 'approve'}
        title={`Approve ${selectedRows.filter((r) => r.status === 'pending_review').length} purchase orders?`}
        message="This will move all selected pending POs to Approved status and apply soft reserves."
        onConfirm={handleBulkApprove}
        onCancel={() => setBulkAction(null)}
      />

      {/* Bulk cancel — full modal for reason capture + notify flags. */}
      <CancelPOModal
        open={bulkAction === 'cancel'}
        poLabel={`${selectedRows.filter((r) => (['draft', 'pending_review', 'approved'] as POStatus[]).includes(r.status)).length} purchase orders`}
        testIdPrefix="bulk-cancel"
        onConfirm={handleBulkCancel}
        onCancel={() => setBulkAction(null)}
      />

      {/* Approve confirm */}
      <ConfirmDialog
        open={!!approveConfirm}
        title={`Approve PO ${approveConfirm?.po_number || ''}?`}
        message="This will move the PO to Approved status."
        onConfirm={() => approveConfirm && handleApprovePO(approveConfirm)}
        onCancel={() => setApproveConfirm(null)}
      />

      {/* Row-action confirm dialogs — one <ConfirmDialog> per action, all driven
          by the single rowActionConfirm state. Only the matching action renders
          its "open" as true at any given time. */}
      <ConfirmDialog
        open={rowActionConfirm?.action === 'submit'}
        title={`Submit PO ${rowActionConfirm?.po.po_number || ''} for review?`}
        message="This will move the PO to Pending Review and notify administrators."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />
      <ConfirmDialog
        open={rowActionConfirm?.action === 'delete'}
        title={`Delete PO ${rowActionConfirm?.po.po_number || ''}?`}
        message="This permanently removes the draft PO and all its line items. This cannot be undone."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />
      <ConfirmDialog
        open={rowActionConfirm?.action === 'cancel'}
        title={`Cancel PO ${rowActionConfirm?.po.po_number || ''}?`}
        message="Cancelled POs release any active soft reserve and cannot be reopened."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />
      <ConfirmDialog
        open={rowActionConfirm?.action === 'markOrdered'}
        title={`Mark PO ${rowActionConfirm?.po.po_number || ''} as Ordered?`}
        message="This records that the order was placed with the distributor. Soft reserve remains active."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />
      <ConfirmDialog
        open={rowActionConfirm?.action === 'markAwaitingInvoice'}
        title={`Mark PO ${rowActionConfirm?.po.po_number || ''} as Awaiting Invoice?`}
        message="The PO will hold here until the distributor invoice arrives, then move to Processing."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />
      <ConfirmDialog
        open={rowActionConfirm?.action === 'markProcessing'}
        title={`Mark PO ${rowActionConfirm?.po.po_number || ''} as Processing?`}
        message="Move to Processing. Upload the distributor invoice via Files and enter any actual-cost adjustments."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />
      <ConfirmDialog
        open={rowActionConfirm?.action === 'markInvoiceReceived'}
        title={`Mark PO ${rowActionConfirm?.po.po_number || ''} as Invoice Received?`}
        message="Actual costs will lock and the PO will enter the Receiving queue."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />
      <ConfirmDialog
        open={rowActionConfirm?.action === 'editRequired'}
        title={`Begin Edit Required for PO ${rowActionConfirm?.po.po_number || ''}?`}
        message="This opens the PO detail page where a revision will be created and the original moved to Superseded."
        onConfirm={handleRowAction}
        onCancel={() => setRowActionConfirm(null)}
      />

      {/* Reject modal (admin-only; needs a rejection note textarea) */}
      {rejectTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ zIndex: 'var(--z-modal, 200)' }}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/60 cursor-default"
            onClick={() => {
              setRejectTarget(null);
              setRejectNote('');
            }}
            aria-label="Close dialog"
            tabIndex={-1}
          />
          <div
            className="relative bg-surface-1 border border-border rounded-xl shadow-lg w-full max-w-md p-6"
            role="dialog"
            aria-modal="true"
            aria-label={`Reject PO ${rejectTarget.po_number}`}
          >
            <h3 className="text-sm font-semibold text-ink-bright mb-3">
              Reject PO {rejectTarget.po_number}
            </h3>
            <label
              htmlFor="po-list-reject-note"
              className="block text-xs font-medium text-ink-secondary mb-1.5"
            >
              Rejection Note (optional)
            </label>
            <textarea
              id="po-list-reject-note"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              className="arbi-input w-full text-xs h-20 resize-none"
              placeholder="Reason for rejection..."
            />
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-border-subtle">
              <button
                onClick={() => {
                  setRejectTarget(null);
                  setRejectNote('');
                }}
                className="arbi-btn-ghost text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="arbi-btn-primary text-xs bg-danger hover:bg-danger/80"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
